import {Injectable} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {DashboardActions} from "../actions";
import {catchError, map, mergeMap} from "rxjs";
import {ApiError, DashboardService} from "@esa/api";
import {HttpClient, HttpParams} from "@angular/common/http";
import {environment} from "../../../../environments/environment";
import {ToastrService} from "ngx-toastr";
import {ApiErrorHandlerService} from "../../services";
import {getRegionReportSuccess} from "../actions/dashboard.actions";

@Injectable()
export class DashboardEffects {
  private buildParams(action: any): HttpParams {
    let params = new HttpParams()
      .set('SearchTerm', action.searchTerm || '')
      .set('pageNo', (action.pageNumber || 1).toString())
      .set('pageSize', (action.pageSize || 10).toString());
    
    if (action.caseStatus) params = params.set('caseStatus', action.caseStatus);
    if (action.casePriority) params = params.set('casePriority', action.casePriority);
    if (action.assignedUserId) params = params.set('assignedUserId', action.assignedUserId);
    if (action.targetFiuId) params = params.set('targetFiuId', action.targetFiuId);
    if (action.dateFrom) params = params.set('dateFrom', new Date(action.dateFrom).toISOString());
    if (action.dateTo) params = params.set('dateTo', new Date(action.dateTo).toISOString());
    if (action.isOverdue) params = params.set('isOverdue', action.isOverdue);
    
    return params;
  }

  getIncomingDashboardCaseFiles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.getIncomingDashCaseFiles),
      mergeMap((action) => this.http.get<any>(`${environment.apiUrl}/api/dashboard/activeCasesIncoming`, { params: this.buildParams(action) }).pipe(
        map(response => DashboardActions.getIncomingDashCaseSuccess({
          incomingDashCaseFiles: response.data,
          incomingPagedResponse: {
            totalCount: response.totalCount,
            pagedCount: response.pagedCount
          }
        })),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, DashboardActions.getIncomingDashCaseError());
        })
      ))
    );
  });

  getOutgoingDashboardCaseFiles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.getOutgoingDashCaseFiles),
      mergeMap((action) => this.http.get<any>(`${environment.apiUrl}/api/dashboard/activeCasesOutgoing`, { params: this.buildParams(action) }).pipe(
        map(response => DashboardActions.getOutgoingDashCaseSuccess({
          outgoingDashCaseFiles: response.data,
          outgoingPagedResponse: {
            totalCount: response.totalCount,
            pagedCount: response.pagedCount
          }
        })),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, DashboardActions.getOutgoingDashCaseError());
        })
      ))
    );
  });

  getArchivedDashboardCaseFiles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.getArchivedDashCaseFiles),
      mergeMap((action) => this.http.get<any>(`${environment.apiUrl}/api/dashboard/archivedCases`, { params: this.buildParams(action) }).pipe(
        map(response => DashboardActions.getArchivedDashCaseSuccess({
          archivedCaseFiles: response.data,
          archivedPagedResponse: {
            totalCount: response.totalCount,
            pagedCount: response.pagedCount
          }
        })),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, DashboardActions.getArchivedDashCaseError());
        })
      ))
    );
  });

  getActiveDashboardCaseFiles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.getActiveDashCaseFiles),
      mergeMap((action) => this.http.get<any>(`${environment.apiUrl}/api/dashboard/activeCases`, { params: this.buildParams(action) }).pipe(
        map(response => DashboardActions.getActiveDashCaseSuccess({
          activeCaseFiles: response.data,
          activePagedResponse: {
            totalCount: response.totalCount,
            pagedCount: response.pagedCount
          }
        })),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, DashboardActions.getActiveDashCaseError());
        })
      ))
    );
  });

  getClosedDashboardCaseFiles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.getClosedDashCaseFiles),
      mergeMap((action) => this.http.get<any>(`${environment.apiUrl}/api/dashboard/closedCases`, { params: this.buildParams(action) }).pipe(
        map(response => DashboardActions.getClosedDashCaseSuccess({
          closedCaseFiles: response.data,
          closedPagedResponse: {
            totalCount: response.totalCount,
            pagedCount: response.pagedCount
          }
        })),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, DashboardActions.getClosedDashCaseError());
        })
      ))
    );
  });

  getDashboardRegionCases$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.getRegionCases),
      mergeMap((action) => this._dashboardService.getApiDashboardRegionCases(action.timeframe).pipe(
        map(response => DashboardActions.getRegionCasesSuccess({regionCases: response})),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, DashboardActions.getRegionCasesError());
        })

      ))
    );
  });

  getDashboardRegionReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DashboardActions.getRegionReport),
      mergeMap(({}) => this._dashboardService.getApiDashboardRegionCaseReports().pipe(
        map(response => DashboardActions.getRegionReportSuccess({ regionReport: response })),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, DashboardActions.getRegionReportError());
        })
      ))
    );
  });

  constructor(
    private actions$: Actions,
    private _dashboardService: DashboardService,
    private http: HttpClient,
    private toastrService: ToastrService,
    private apiErrorHandlerService: ApiErrorHandlerService
  ) {}
}
